/* eslint-disable react/no-unknown-property */
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import {
  Grid, ICON_FORMAT_LIST_BULLETED_SQUARE, ICON_MAGNIFY, ICON_PHONE_OUTLINE, ICON_PLUS,
  ICON_ACCOUNT_SEARCH_OUTLINE,
  ICON_COG_OUTLINE,
  ICON_MAP_SEARCH_OUTLINE,
  ICON_ACCOUNT_MULTIPLE_OUTLINE,
  Action,
  Stack,
  ICON_MESSAGE_PROCESSING_OUTLINE,
  ICON_HEART_OUTLINE,
  ICON_HOME_PLUS_OUTLINE,
  ICON_ARROW_UP,
  ICON_DOMAIN,
  ICON_MAP_CHECK_OUTLINE,
  ICON_ACCOUNT_COG_OUTLINE,
  ICON_ACCOUNT_OUTLINE,
  ICON_CASH_CHECK,
  Card,
} from '@doveit/bricks';
import { useNavigate } from 'react-router-dom';
import GenericPageLayout from '../../layouts/generic-page-layout/GenericPageLayout';
import { NavigationItem } from '../../types';
import Xmas, { isXmasTime } from './Xmas';
import * as styles from './DashboardPage.style';
import useRBAC from '../../hooks/use-rbac/useRBAC';
import { useCurrentAgentSync } from '../../hooks/use-agent/useAgent';
import AgentGoalWidget from '../../agent/containers/agent-goal-widget/AgentGoalWidget';
import { GenericPageLayoutWrapperSize } from '../../layouts/generic-page-layout/wrapper/GenericPageLayoutWrapper';
import PageSkeleton from '../../components/page-skeleton/PageSkeleton';
import AgentRankWidget from '../../agent/containers/agent-rank-widget/AgentRankWidget';
import ManagerGoalWidget from '../../agent/containers/manager-goal-widget/ManagerGoalWidget';
import ManagerRankWidget from '../../agent/containers/manager-rank-widget/ManagerRankWidget';
import ViewAgentHeader from '../../agent/containers/view-agent-header/ViewAgentHeader';
import { JobTitle } from '../../domain/types';
import { removeNullAndUndefinedValues } from '../../utils/array';
import useUserPermissions from '../../hooks/use-user-permissions/useUserPermissions';

type PanelType = {
  id: string,
  icon: string,
  title: string,
  renderConditions: boolean,
  menu: NavigationItem[],
};

const DashboardPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    user,
    userIsAdmin,
    userIsAgent,
    userIsCallCenter,
    userIsContentEditor,
    userIsFinance,
    userIsHR,
  } = useRBAC();
  const { data: currentAgent, isLoading: isCurrentAgentLoading } = useCurrentAgentSync();
  const userPermissions = useUserPermissions(['contacts', 'rumors', 'jobApplications']);

  const goToPage = useCallback((url: string) => navigate(url), [navigate]);

  const showProspectsPanel = userIsAdmin || userIsAgent || userIsCallCenter;
  const showGeographyPanel = userIsAdmin || userIsCallCenter || userIsContentEditor || userIsHR;
  const showLeadsPanel = userIsAdmin || userIsCallCenter;
  const showAdministrationPanel = userIsAdmin || userIsFinance || userIsCallCenter || userIsContentEditor || userIsHR;
  // const showHRPanel = true;
  const enableAdminMenuItem = userIsAdmin;
  const enableFinanceMenuItem = userIsFinance;
  const enableRumorMenu = userIsAdmin || (userIsAgent && !currentAgent?.inTrial) || userIsCallCenter;
  // const enableManageJobApplications = userIsAdmin || userIsHR || userIsAgent;

  const isEligibleForAgentDashboard = currentAgent
    && !currentAgent.inTrial
    && ![JobTitle.AREA_MANAGER, JobTitle.BROKER, JobTitle.EXECUTIVE].includes(currentAgent.jobTitle) && !currentAgent.contract.endDate;

  const showAgentGoalWidget = isEligibleForAgentDashboard && (currentAgent.configuration?.dashboard?.canViewPerformance ?? true);
  const showAgentRankWidget = isEligibleForAgentDashboard && (currentAgent.configuration?.dashboard?.canViewRanking ?? true);

  const isEligibleForManagerDashboard = currentAgent
    && !currentAgent.contract.endDate
    && ([JobTitle.AREA_MANAGER, JobTitle.BROKER].includes(currentAgent.jobTitle)
      || (currentAgent?.jobTitle === JobTitle.EXECUTIVE && (currentAgent?.canManage?.length ?? 0) > 0));

  const showManagerGoalWidget = isEligibleForManagerDashboard && (currentAgent.configuration?.dashboard?.canViewPerformance ?? true);
  const showManagerRankWidget = isEligibleForManagerDashboard && (currentAgent.configuration?.dashboard?.canViewRanking ?? true);

  const showMenu = !isEligibleForAgentDashboard && !isEligibleForManagerDashboard;

  const isXmas = useMemo(() => isXmasTime(), []);
  const panels: PanelType[] = useMemo(() => [
    {
      id: 'call-center-panel',
      icon: ICON_PHONE_OUTLINE,
      title: 'Call Center',
      renderConditions: !!showLeadsPanel,
      menu: [
        {
          id: 'add-lead',
          label: 'Aggiungi lead',
          icon: ICON_PLUS,
          path: '/call-center/contacts/create',
        },
        {
          id: 'handle-call-center',
          label: 'Gestisci chiamate',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
          path: '/call-center/contacts/process',
        },
        {
          id: 'messaging',
          label: 'Centro messaggi',
          icon: ICON_MESSAGE_PROCESSING_OUTLINE,
          path: '/call-center/messaging',
        },
      ],
    },
    {
      id: 'contacts-panel',
      icon: ICON_ACCOUNT_OUTLINE,
      renderConditions: !!userPermissions.contacts.canSearch,
      title: 'Contatti',
      menu: [{
        id: 'search-contact',
        label: 'Cerca contatto',
        icon: ICON_MAGNIFY,
        path: '/contacts/search',
      }],
    },
    {
      id: 'intents-panel',
      icon: ICON_HEART_OUTLINE,
      title: 'Interessi',
      renderConditions: !!user,
      menu: [
        {
          id: 'add-intent',
          label: 'Aggiungi interesse',
          icon: ICON_PLUS,
          path: '/intents/create',
        },
        {
          id: 'handle-intents',
          label: 'Gestisci interessi',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
          path: '/intents/explore',
        },
        {
          id: 'search-criteria-add',
          label: 'Aggiungi ricerca',
          icon: ICON_HOME_PLUS_OUTLINE,
          path: '/search-criteria/create',
        },
      ],
    },
    {
      id: 'prospects-panel',
      icon: ICON_ARROW_UP,
      title: 'Acquisizione',
      renderConditions: !!showProspectsPanel,
      menu: removeNullAndUndefinedValues([
        userPermissions.rumors.canAdd ? {
          id: 'add-rumor',
          label: 'Aggiungi notizia',
          icon: ICON_PLUS,
          path: '/rumors/create',
          disabled: !enableRumorMenu,
        } : undefined,
        {
          id: 'handle-rumors',
          label: 'Gestisci notizie',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
          path: '/rumors/explore',
          disabled: !enableRumorMenu,
        },
        {
          id: 'handle-prospects',
          label: 'Gestisci valutazioni',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
          path: '/prospects/explore',
        },
      ]),
    },
    {
      id: 'properties-panel',
      icon: ICON_DOMAIN,
      title: 'Immobili',
      renderConditions: !!user,
      menu: [
        {
          id: 'handle-properties',
          label: 'Gestisci immobili',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
          path: '/properties/explore',
        },
      ],
    },
    {
      id: 'geographies-panel',
      icon: ICON_MAP_CHECK_OUTLINE,
      title: 'Geografie',
      renderConditions: !!showGeographyPanel,
      menu: [
        {
          id: 'search-agent-by-cap',
          label: 'Cerca CAP',
          icon: ICON_MAGNIFY,
          path: '/agent/search/zipcode',
        },
        {
          id: 'search-agent-by-locality',
          label: 'Cerca città',
          icon: ICON_MAP_SEARCH_OUTLINE,
          path: '/agent/search/locality',
        },
        {
          id: 'search-agent-by-areas',
          label: 'Cerca agente',
          icon: ICON_ACCOUNT_SEARCH_OUTLINE,
          path: '/agent/search/areas',
        },
      ],
    },
    {
      id: 'administration-panel',
      icon: ICON_COG_OUTLINE,
      title: 'Amministrazione',
      renderConditions: !!showAdministrationPanel,
      menu: [
        {
          id: 'handle-agents',
          label: 'Gestisci agenti',
          icon: ICON_ACCOUNT_COG_OUTLINE,
          path: '/agents/explore',
          disabled: !(userIsAdmin || userIsContentEditor || userIsCallCenter || userIsHR || userIsContentEditor),
        },
        {
          id: 'invoices-explore',
          label: 'Gestisci pagamenti',
          icon: ICON_CASH_CHECK,
          path: '/invoices/explore',
          disabled: !enableAdminMenuItem && !enableFinanceMenuItem,
        },
        {
          id: 'create-agency',
          label: 'Aggiungi agenzia',
          icon: ICON_HOME_PLUS_OUTLINE,
          path: '/agencies/create',
          disabled: !enableAdminMenuItem,
        },
      ],
    },
    {
      id: 'hr-panel',
      icon: ICON_ACCOUNT_MULTIPLE_OUTLINE,
      title: 'HR',
      renderConditions: (userPermissions.jobApplications.canAdd || userPermissions.jobApplications.canExploreAll),
      menu: [
        {
          id: 'add-job-application',
          label: 'Aggiungi candidatura',
          icon: ICON_PLUS,
          path: '/job-applications/create',
        },
        {
          id: 'handle-job-applications',
          label: 'Gestisci candidature',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
          path: '/job-applications/explore',
        },
      ],
    },
  ], [enableAdminMenuItem, enableFinanceMenuItem, enableRumorMenu, userPermissions.contacts.canSearch, userPermissions.jobApplications.canAdd, userPermissions.jobApplications.canExploreAll, userPermissions.rumors.canAdd, showAdministrationPanel, showGeographyPanel, showLeadsPanel, showProspectsPanel, user, userIsAdmin, userIsCallCenter, userIsContentEditor, userIsHR]);

  if (isCurrentAgentLoading) {
    return <PageSkeleton />;
  }

  return (
    <GenericPageLayout>
      {currentAgent && (
        <GenericPageLayout.Bar wrapperSize={GenericPageLayoutWrapperSize.LARGE}>
          <ViewAgentHeader agent={currentAgent} />
        </GenericPageLayout.Bar>
      )}
      <GenericPageLayout.Content wrapperSize={GenericPageLayoutWrapperSize.LARGE}>
        {user && showMenu && (
          <div
            css={styles.user}
            isXmasTime={isXmas}
          >
            {isXmas && (
              <Xmas />
            )}
            <h1>
              {isXmas ? 'Buone feste' : 'Bentornato'} <strong>{user.name}</strong>
            </h1>
          </div>
        )}

        <Grid gutter={150}>
          {showAgentGoalWidget && (
            <Grid.Unit>
              <AgentGoalWidget agentId={currentAgent?.id!} />
            </Grid.Unit>
          )}
          {showManagerGoalWidget && (
            <Grid.Unit>
              <ManagerGoalWidget agentId={currentAgent?.id!} />
            </Grid.Unit>
          )}
          {showAgentRankWidget && (
            <Grid.Unit>
              <AgentRankWidget agentId={currentAgent?.id!} />
            </Grid.Unit>
          )}
          {showManagerRankWidget && (
            <Grid.Unit>
              <ManagerRankWidget agentId={currentAgent?.id!} />
            </Grid.Unit>
          )}
          {showMenu && panels.map((panel) => panel.renderConditions && (
            <Grid.Unit
              key={panel.id}
              size={{ LG: 1 / 3 }}
            >
              <Card css={styles.panelCard}>
                <Card.Header
                  icon={{ path: panel.icon }}
                  title={panel.title}
                />
                <Card.Content>
                  <Stack>
                    {panel.menu.map((menuItem) => (
                      <div key={menuItem.id}>
                        <Action
                          label={menuItem.label}
                          iconLeft={menuItem.icon ? { path: menuItem.icon } : undefined}
                          alignment="left"
                          color="neutral"
                          onClick={() => goToPage(menuItem.path!)}
                          disabled={menuItem.disabled}
                          expanded
                        />
                      </div>
                    ))}
                  </Stack>
                </Card.Content>
              </Card>
            </Grid.Unit>
          ))}
        </Grid>
      </GenericPageLayout.Content>
    </GenericPageLayout>
  );
};

export default DashboardPage;
